import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useFlagsWithInit } from './useFlags';
import { updateSessionContext } from '../services/guestAiPromptSessions';
import { backendToFrontendFilters } from '../utils/filterMappings';

/**
 * Hook for managing property filters with AI context sync and persistence
 */
export function usePropertyFilters({
  sessionId,
  initialFilters = null,
  onFiltersChange = null,
  messages = null,
  session = null
}) {
  const { isAiContextSyncEnabled } = useFlagsWithInit();
  const [filters, setFilters] = useState({
    location: '',
    priceMin: '',
    priceMax: '',
    bedrooms: '',
    bathrooms: '',
    areaMin: '',
    areaMax: '',
    propertyTypes: [],
    conditions: [],
    features: []
  });

  const [isExpanded, setIsExpanded] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const syncTimeoutRef = useRef(null);
  const hasInitialized = useRef(false); // Track if we've loaded from DB once

  // Helper function to extract AI search defaults from session
  const extractAiSearchDefaults = useCallback((session, messages) => {
    if (!session?.parsed_json) return {};

    // Priority 1: Load from parsed_json.user_filters (user's manual selections)
    // Priority 2: Load from parsed_json.properties_data (AI filters)
    const userFilters = session.parsed_json.user_filters;
    const aiFilters = session.parsed_json.properties_data;
    const locationData = session.parsed_json.location_data;

    const sourceData = userFilters || aiFilters || {};

    if (!sourceData || Object.keys(sourceData).length === 0) {
      if (locationData) {
        return backendToFrontendFilters({}, locationData);
      }
      return {};
    }

    return backendToFrontendFilters(sourceData, locationData);
  }, []);

  // Helper function to merge user filters with AI defaults
  const mergeWithAiDefaults = useCallback((userFilters, aiDefaults) => {
    const baseFilters = {
      location: '',
      priceMin: '',
      priceMax: '',
      bedrooms: '',
      bathrooms: '',
      areaMin: '',
      areaMax: '',
      propertyTypes: [],
      conditions: [],
      features: []
    };

    // Merge: base -> AI defaults -> user filters (user filters have highest priority)
    return { ...baseFilters, ...aiDefaults, ...userFilters };
  }, []);

  // Initialize filters from session.parsed_json.properties_data (database as source of truth)
  // ONLY runs once when session data is available to avoid overwriting user changes
  useEffect(() => {
    // Only initialize once when session first loads
    if (hasInitialized.current) {
      return;
    }

    // Wait for session.parsed_json to be loaded (not just empty session object)
    if (!session?.parsed_json || !session?.parsed_json?.properties_data) {
      return;
    }

    try {
      // Extract AI search defaults from session and messages
      const aiDefaults = extractAiSearchDefaults(session, messages);

      // Merge user filters with AI defaults (user filters take priority)
      const mergedFilters = mergeWithAiDefaults(initialFilters, aiDefaults);

      if (mergedFilters && Object.keys(mergedFilters).length > 0) {
        setFilters((prev) => ({
          ...prev,
          ...mergedFilters
        }));
      }
    } catch (error) {
      console.error('❌ Error initializing filters:', error);
      // Fallback to initialFilters if available
      if (initialFilters && typeof initialFilters === 'object') {
        setFilters((prev) => ({
          ...prev,
          ...initialFilters
        }));
      }
    }

    // Mark as initialized
    hasInitialized.current = true;
  }, [session, initialFilters, messages, extractAiSearchDefaults, mergeWithAiDefaults]);

  // Count active ADVANCED filters only
  // Advanced filters are: areaMin, areaMax, propertyTypes, conditions, features
  const activeFilterCount = useMemo(() => {
    return Object.entries(filters).reduce((count, [key, value]) => {
      // Skip main filters (location, priceMin, priceMax, bedrooms, bathrooms)
      if (['location', 'priceMin', 'priceMax', 'bedrooms', 'bathrooms'].includes(key)) {
        return count;
      }

      // Count array filters by their length
      if (key === 'propertyTypes' || key === 'features' || key === 'conditions') {
        return count + (Array.isArray(value) ? value.length : 0);
      }

      // Count other advanced filters (areaMin, areaMax) if they have values
      return count + (value ? 1 : 0);
    }, 0);
  }, [filters]);

  // Update individual filter
  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value
    }));
  }, []);

  // Debounced AI context sync
  const syncWithAI = useCallback(
    (filtersToSync) => {
      if (!isAiContextSyncEnabled || !sessionId) return;

      // Clear existing timeout
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }

      // Set new timeout for debounced sync
      syncTimeoutRef.current = setTimeout(async () => {
        try {
          setIsSyncing(true);
          await updateSessionContext(sessionId, {
            userFilters: filtersToSync,
            messages
          });
        } catch (error) {
          console.error('Failed to sync filters with AI:', error);
        } finally {
          setIsSyncing(false);
        }
      }, 1000); // 1 second debounce
    },
    [isAiContextSyncEnabled, sessionId, messages]
  );

  // Clear all filters
  const clearFilters = useCallback(() => {
    const clearedFilters = {
      location: '',
      priceMin: '',
      priceMax: '',
      bedrooms: '',
      bathrooms: '',
      areaMin: '',
      areaMax: '',
      propertyTypes: [],
      conditions: [],
      features: []
    };
    setFilters(clearedFilters);

    // Sync cleared filters with AI if enabled
    if (isAiContextSyncEnabled && sessionId) {
      syncWithAI(clearedFilters);
    }

    if (onFiltersChange) {
      onFiltersChange(clearedFilters);
    }
  }, [isAiContextSyncEnabled, sessionId, onFiltersChange, syncWithAI]);

  // Create filter description from current filters only
  const createFilterDescription = useCallback((currentFilters) => {
    const filtersToApply = {
      location: currentFilters.location || '',
      priceMin: currentFilters.priceMin || '',
      priceMax: currentFilters.priceMax || '',
      bedrooms: currentFilters.bedrooms || '',
      bathrooms: currentFilters.bathrooms || '',
      areaMin: currentFilters.areaMin || '',
      areaMax: currentFilters.areaMax || '',
      propertyTypes: currentFilters.propertyTypes || [],
      conditions: currentFilters.conditions || [],
      features: currentFilters.features || []
    };

    const filterParts = [];

    if (filtersToApply.location) filterParts.push(`📍 ${filtersToApply.location}`);

    if (filtersToApply.priceMin || filtersToApply.priceMax) {
      if (filtersToApply.priceMin && filtersToApply.priceMax) {
        filterParts.push(
          `💰 Budget €${Number(filtersToApply.priceMin).toLocaleString()} - €${Number(filtersToApply.priceMax).toLocaleString()}`
        );
      } else if (filtersToApply.priceMin) {
        filterParts.push(`💰 Minimum budget €${Number(filtersToApply.priceMin).toLocaleString()}`);
      } else {
        filterParts.push(`💰 Maximum budget €${Number(filtersToApply.priceMax).toLocaleString()}`);
      }
    }

    if (filtersToApply.bedrooms) filterParts.push(`🛏️ ${filtersToApply.bedrooms}+ bedrooms`);
    if (filtersToApply.bathrooms) filterParts.push(`🚿 ${filtersToApply.bathrooms}+ bathrooms`);

    if (filtersToApply.areaMin || filtersToApply.areaMax) {
      const areaRange = [];
      if (filtersToApply.areaMin) areaRange.push(`${filtersToApply.areaMin}m²`);
      if (filtersToApply.areaMax) areaRange.push(`${filtersToApply.areaMax}m²`);
      filterParts.push(`📐 ${areaRange.join(' - ')}`);
    }

    if (filtersToApply.propertyTypes?.length) {
      filterParts.push(`🏠 ${filtersToApply.propertyTypes.join(', ')}`);
    }

    if (filtersToApply.conditions?.length) {
      filterParts.push(`🔧 ${filtersToApply.conditions.join(', ')}`);
    }

    if (filtersToApply.features?.length) {
      filterParts.push(`✨ ${filtersToApply.features.join(', ')}`);
    }

    return {
      filtersToApply,
      description: filterParts.length > 0 ? filterParts.join(' • ') : 'All properties'
    };
  }, []);

  // Apply filters (called when Search button is clicked)
  const applyFilters = useCallback(async () => {
    setIsApplyingFilters(true);

    try {
      const { filtersToApply, description } = createFilterDescription(filters);

      // Create a USER message for filter updates so AI can respond naturally
      // Using role: 'user' instead of 'system' allows AI to acknowledge the changes
      const filterMessage = {
        role: 'user',
        content: `I've updated my search filters: ${description}`,
        // Store metadata separately for UI purposes (not sent to OpenAI)
        _metadata: {
          type: 'filter_update',
          filters: filtersToApply,
          timestamp: new Date().toISOString()
        }
      };

      // Notify parent component with filters
      // React Query will automatically refetch when activeFilters state changes
      if (onFiltersChange) {
        await onFiltersChange({
          filterMessage,
          filters: filtersToApply,
          isFilterUpdate: true
        });
      }
    } catch (error) {
      console.error('❌ Error applying filters:', error);
    } finally {
      // Add a small delay to show loading state
      setTimeout(() => {
        setIsApplyingFilters(false);
      }, 500);
    }
  }, [filters, createFilterDescription, onFiltersChange]);

  // Toggle expanded state
  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);
  // Refresh filters from database (session.parsed_json.properties_data)
  // This is called explicitly when AI updates the database
  const refreshFiltersFromAI = useCallback(() => {
    try {
      // Extract AI search defaults from current session and messages
      const aiDefaults = extractAiSearchDefaults(session, messages);

      // Only merge if we have meaningful AI defaults
      if (aiDefaults && Object.keys(aiDefaults).length > 0) {
        // Merge current user filters with fresh AI defaults (AI defaults have lower priority)
        const mergedFilters = mergeWithAiDefaults({}, aiDefaults); // Start fresh to get AI defaults

        setFilters((prev) => ({
          ...prev,
          ...mergedFilters
        }));
      } else {
        console.log('⚠️ No properties_data found in parsed_json');
      }
    } catch (error) {
      console.error('❌ Error refreshing filters from database:', error);
    }
  }, [session, messages, extractAiSearchDefaults, mergeWithAiDefaults]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  return {
    filters,
    updateFilter,
    clearFilters,
    applyFilters,
    activeFilterCount,
    isExpanded,
    setIsExpanded,
    toggleExpanded,
    isSyncing,
    isApplyingFilters,
    refreshFiltersFromAI
  };
}
